import { zodResolver } from '@hookform/resolvers/zod'
import { Box, CircularProgress, Grid, Typography } from '@mui/joy'
import { useStore } from 'effector-react'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FreeContractTypeConfirmModal } from '~/entities/common/RentalContract/ui/FreeContractTypeConfirmModal'
import { PriceConfirmModal } from '~/entities/common/RentalContract/ui/PriceConfirmModal'
import { RentalService, userOptionsStore } from '~/shared/api'
import {
  daysEnumOptions,
  rentalContractQuantityOfHoursEnumOptions,
  rentalContractTypeEnumOptions,
  rentalContractWorkScheduleEnumOptions,
} from '~/shared/config/constants'
import {
  RentalContractTypeEnum,
  RentalContractWorkScheduleEnum,
  TaxiparkCountryEnum,
} from '~/shared/config/enums'
import {
  getObject,
  isObject,
  isString,
  sanitizeStringNumber,
} from '~/shared/lib/utils'
import { Alert } from '~/shared/ui/Alerts'
import {
  AsyncAutocompleteInput,
  AutocompleteInput,
  DatePickerInput,
  DateTimePickerInput,
  FormButtons,
  formButtonsModel,
  FormProvider,
  NumericInput,
  Switch,
  TextInput,
} from '~/shared/ui/Form'
import { Container, ContainerTitle } from '~/shared/ui/Layout'
import { useModal } from '~/shared/ui/Modal'
import {
  calculateRentPerDay,
  calculateRentPerDayFx,
  fetchCarOptions,
  formSchema,
  FormValues,
} from './model'

type FormProps = {
  isCreating?: boolean
  canEdit?: boolean
  isSubmitting?: boolean
  isLoading?: boolean
  onSuccess?: (formValues: FormValues) => void
  defaultValues?: Partial<FormValues>
  isBookedUntilVisible?: boolean
  canFirstDayIsFreeEdit?: boolean
  canSomeFieldEdit?: boolean
  canUpdateRentPrice?: boolean
  canSetContractType?: boolean
  canCalculateRentPerDay?: boolean
  canRentalServicesEdit?: boolean
}

export function Form({
  isCreating,
  canEdit,
  isSubmitting,
  isLoading,
  defaultValues,
  onSuccess,
  isBookedUntilVisible,
  canFirstDayIsFreeEdit,
  canSomeFieldEdit,
  canUpdateRentPrice,
  canSetContractType,
  canCalculateRentPerDay,
  canRentalServicesEdit,
}: FormProps) {
  const { t } = useTranslation()
  const {
    isModalShow: isPriceModalShow,
    openModal: openPriceModal,
    closeModal: closePriceModal,
  } = useModal()
  const {
    isModalShow: isFreeTypeModalShow,
    openModal: openFreeTypeModal,
    closeModal: closeFreeTypeModal,
  } = useModal()

  const [calculatedRentPerDay, setCalculatedRentPerDay] = useState<
    number | undefined
  >(undefined)
  const [pendingFormValues, setPendingFormValues] = useState<
    FormValues | undefined
  >(undefined)

  const isEditing = useStore(formButtonsModel.$isEditing)
  const disabled = !isCreating || isSubmitting
  const isLoadingRentPerDay = useStore(calculateRentPerDayFx.pending)
  const isCI = userOptionsStore.country === TaxiparkCountryEnum.CI
  const isRU = userOptionsStore.country === TaxiparkCountryEnum.RU
  const form = useForm<FormValues>({
    resolver: zodResolver(
      !isCreating ? formSchema.omit({ bookedUntil: true }) : formSchema,
    ),
    defaultValues,
  })

  const { watch, setValue } = form

  const formRentPerDay = sanitizeStringNumber(watch('rentPerDay'))

  const workScheduleOption = watch('workSchedule')
  const workSchedule = getObject(workScheduleOption)?.id

  const contractTypeOption = watch('contractType')
  const contractType = (getObject(contractTypeOption)?.id ??
    (isString(contractTypeOption) ? contractTypeOption : undefined)) as
    | RentalContractTypeEnum
    | undefined

  const formCarOption = watch('carOption')
  const carOption = useMemo(
    () => (isObject(formCarOption) ? formCarOption : undefined),
    [formCarOption],
  )

  const isDayOff =
    workSchedule === RentalContractWorkScheduleEnum.SIX_THROUGH_ONE

  const isRentPerDayLockedByContractType =
    isRU && contractType === RentalContractTypeEnum.FREE

  const isCustomRentPerDay = useMemo(() => {
    if (isRentPerDayLockedByContractType || !canCalculateRentPerDay) {
      return false
    }
    if (
      formRentPerDay === undefined ||
      formRentPerDay === null ||
      Number.isNaN(formRentPerDay)
    ) {
      return false
    }
    return formRentPerDay !== calculatedRentPerDay
  }, [
    calculatedRentPerDay,
    canCalculateRentPerDay,
    formRentPerDay,
    isRentPerDayLockedByContractType,
  ])

  const workRuleTitle = defaultValues?.workRule || carOption?.workRule

  useEffect(() => {
    const subscription = watch((data, { name, type }) => {
      if (type !== 'change') return

      if (name === 'carOption') {
        const carOption = isObject(data.carOption) ? data.carOption : undefined
        setValue('brand', carOption?.brand)
        setValue('model', carOption?.model)
        setValue('rental', carOption?.rental)
      }
    })
    return () => subscription.unsubscribe()
  }, [setValue, watch])

  const handleRentPerDaySet = (resRentPerDay?: number) => {
    setCalculatedRentPerDay(resRentPerDay)

    // 0 — валидная цена с бэка (например FREE), не отбрасываем через truthy-check
    if (typeof resRentPerDay === 'number' && !isCustomRentPerDay) {
      setValue('rentPerDay', resRentPerDay)
    }
  }

  useEffect(() => {
    if (!canCalculateRentPerDay) return

    calculateRentPerDay({
      fn: handleRentPerDaySet,
      carId: carOption?.id,
      workRuleId: carOption?.workRuleId,
      workSchedule,
      contractType,
    })
    // eslint-disable-next-line
  }, [
    carOption?.id,
    carOption?.workRuleId,
    workSchedule,
    contractType,
    isEditing,
    isCreating,
  ])

  useEffect(() => {
    if (defaultValues) {
      form.reset(defaultValues)
    }
    // eslint-disable-next-line
  }, [defaultValues, isEditing])

  const handleFormSubmit = (formValues: FormValues) => {
    if (isCreating && contractType === RentalContractTypeEnum.FREE) {
      setPendingFormValues(formValues)
      openFreeTypeModal()
      return
    }

    if (isCustomRentPerDay) {
      setPendingFormValues(formValues)
      openPriceModal()
      return
    }

    onSuccess?.(formValues)
  }

  const handleConfirmPendingSubmit = () => {
    onSuccess?.(pendingFormValues!)
    setPendingFormValues(undefined)
    closePriceModal()
    closeFreeTypeModal()
  }

  const handleCancelPendingSubmit = () => {
    setPendingFormValues(undefined)
    closePriceModal()
    closeFreeTypeModal()
  }

  return (
    <FormProvider form={form} onSuccess={handleFormSubmit} canEdit={canEdit}>
      <Container>
        <ContainerTitle>{t('basic information')}</ContainerTitle>
        <Grid container spacing={2.5}>
          <Grid xs={6}>
            <TextInput
              label='form.driver'
              name='driverFullName'
              skeletonShow={isLoading}
              readOnly
            />
          </Grid>

          <Grid xs={6}>
            <TextInput
              label='form.lessor'
              name='rental'
              placeholder='form.lessor'
              skeletonShow={isLoading}
              readOnly
            />
          </Grid>

          <Grid xs={6}>
            <AsyncAutocompleteInput
              label='form.car'
              placeholder='form.select car'
              name='carOption'
              fetchOptions={fetchCarOptions}
              skeletonShow={isLoading}
              readOnly={disabled}
            />
          </Grid>
          <Grid xs={3}>
            <TextInput
              label='form.brand'
              placeholder='form.brand'
              name='brand'
              readOnly
              skeletonShow={isLoading}
            />
          </Grid>
          <Grid xs={3}>
            <TextInput
              label='form.model'
              placeholder='form.model'
              name='model'
              readOnly
              skeletonShow={isLoading}
            />
          </Grid>

          <Grid xs={12}>
            <Alert variant='info' isLoading={isLoading} data-testid='workRule'>
              <Box flexDirection='column'>
                <Typography fontWeight={600} fontSize={18}>
                  {t('form.working condition')}
                </Typography>
                <Typography fontSize={14}>
                  {workRuleTitle ||
                    t('alert.select a car to view the working condition')}
                </Typography>
              </Box>
            </Alert>
          </Grid>
          <Grid xs={6}>
            <Switch
              labelI18n='form.first rental day free'
              name='firstDayIsFree'
              skeletonShow={isLoading}
              readOnly={!canFirstDayIsFreeEdit || (!isEditing && disabled)}
            />
          </Grid>
          <Grid xs={6} />
          <Grid xs={6}>
            <AutocompleteInput
              label='form.work schedule'
              placeholder='form.select work schedule'
              name='workSchedule'
              options={rentalContractWorkScheduleEnumOptions}
              readOnly={!isEditing && disabled}
              skeletonShow={isLoading}
            />
          </Grid>
          {isRU ? (
            <Grid xs={6}>
              <AutocompleteInput
                label='form.contract type'
                placeholder='form.select contract type'
                name='contractType'
                options={rentalContractTypeEnumOptions}
                // Тип меняется только при создании и только с правом set rental contract type
                readOnly={!isCreating || isSubmitting || !canSetContractType}
                disableClearable
                skeletonShow={isLoading}
              />
            </Grid>
          ) : (
            <Grid xs={6}>
              {isDayOff && (
                <AutocompleteInput
                  label='form.day off'
                  placeholder='form.select day off'
                  name='dayOff'
                  options={daysEnumOptions}
                  readOnly={!canSomeFieldEdit || (!isEditing && disabled)}
                  skeletonShow={isLoading}
                />
              )}
            </Grid>
          )}
          {isRU && isDayOff && (
            <>
              <Grid xs={6}>
                <AutocompleteInput
                  label='form.day off'
                  placeholder='form.select day off'
                  name='dayOff'
                  options={daysEnumOptions}
                  readOnly={!canSomeFieldEdit || (!isEditing && disabled)}
                  skeletonShow={isLoading}
                />
              </Grid>
              <Grid xs={6} />
            </>
          )}
          <Grid xs={6}>
            <NumericInput
              label='form.daily rental cost'
              placeholder='form.daily rental cost'
              name='rentPerDay'
              readOnly={
                (!isEditing && disabled) ||
                !canUpdateRentPrice ||
                isRentPerDayLockedByContractType
              }
              skeletonShow={isLoading}
              endDecorator={
                isLoadingRentPerDay ? <CircularProgress size='sm' /> : null
              }
            />
          </Grid>
          <Grid xs={6}>
            <AutocompleteInput
              options={rentalContractQuantityOfHoursEnumOptions}
              label='form.quantity of hours'
              name='quantityRentalPayments'
              readOnly={!isCreating}
              skeletonShow={isLoading}
            />
          </Grid>
          <Grid xs={6} />
          {defaultValues?.responsibleManager && (
            <Grid xs={6}>
              <TextInput
                label='form.responsible manager'
                name='responsibleManager'
                readOnly
                skeletonShow={isLoading}
              />
            </Grid>
          )}
          {defaultValues?.signedAt && (
            <Grid xs={3}>
              <TextInput
                label='form.signing date'
                name='signedAt'
                readOnly
                skeletonShow={isLoading}
              />
            </Grid>
          )}
          {defaultValues?.terminationDate && (
            <Grid xs={3}>
              <DatePickerInput
                label='form.termination date'
                name='terminationDate'
                readOnly
                skeletonShow={isLoading}
              />
            </Grid>
          )}
        </Grid>
        {defaultValues?.promotion && (
          <Grid container spacing={2.5}>
            {defaultValues.promotion.discount_percent ? (
              <Grid xs={6}>
                <TextInput
                  label='form.promotion discount percent'
                  name='promotion.discount_percent'
                  readOnly
                  skeletonShow={isLoading}
                />
              </Grid>
            ) : defaultValues.promotion.discount_amount ? (
              <Grid xs={6}>
                <TextInput
                  label='form.promotion discount amount'
                  name='promotion.discount_amount'
                  readOnly
                  skeletonShow={isLoading}
                />
              </Grid>
            ) : null}
            <Grid xs={6}>
              <TextInput
                label='form.promotion duration days'
                name='promotion.duration_days'
                readOnly
                skeletonShow={isLoading}
              />
            </Grid>
          </Grid>
        )}
      </Container>

      <Container>
        <ContainerTitle>{t('form.additional services')}</ContainerTitle>
        <Grid container spacing={2.5}>
          <Grid xs={12}>
            <AsyncAutocompleteInput
              placeholder='form.select additional services'
              name='rentalServicesOptions'
              fetchOptions={RentalService.fetchOptions}
              readOnly={
                isCI || !canRentalServicesEdit || (!isEditing && !isCreating)
              }
              skeletonShow={isLoading}
              multiple
            />
          </Grid>
          <Grid xs={12}>
            <Alert variant='warning'>
              {t(
                'alert.warning the driver will be charged a service fee the first charge will occur immediately after signing the contract',
              )}
            </Alert>
          </Grid>
        </Grid>
      </Container>

      {isBookedUntilVisible && (
        <Container>
          <ContainerTitle>{t('form.booking time')}</ContainerTitle>
          <Grid container spacing={2.5}>
            <Grid xs={6}>
              <DateTimePickerInput
                label='form.booking date and time'
                name='bookedUntil'
                placeholder='form.select booking date and time'
                readOnly={disabled}
                skeletonShow={isLoading}
                maxDays={3}
              />
            </Grid>
            <Grid xs={12}>
              <Alert variant='error'>
                {t(
                  'alert.warning the contract will be annulled if it is not signed before the end of the booking time',
                )}
              </Alert>
            </Grid>
          </Grid>
        </Container>
      )}

      <FormButtons loading={isSubmitting} isCreating={isCreating} />
      <PriceConfirmModal
        isShow={isPriceModalShow}
        onCancel={handleCancelPendingSubmit}
        onConfirm={handleConfirmPendingSubmit}
        isSubmitting={isSubmitting}
        price={pendingFormValues?.rentPerDay || 0}
      />
      <FreeContractTypeConfirmModal
        isShow={isFreeTypeModalShow}
        onCancel={handleCancelPendingSubmit}
        onConfirm={handleConfirmPendingSubmit}
        isSubmitting={isSubmitting}
      />
    </FormProvider>
  )
}
